/**
 * Forms API endpoints.
 *
 * All forms are site-scoped. Each API call requires a siteSlug.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface Form {
  id: string;
  name: string;
  slug: string;
  description?: string;
  settings: FormSettings;
  steps: FormStep[];
  created_at: string;
  updated_at: string;
}

export interface FormSummary {
  id: string;
  name: string;
  slug: string;
  description?: string;
  submission_count: number;
  created_at: string;
  updated_at: string;
}

export interface FormSettings {
  submit_label?: string;
  success_message?: string;
  redirect_url?: string;
  action_url?: string;
  // Notification settings
  notify_emails?: string[];
  send_confirmation?: boolean;
  webhook_url?: string;
  // Spam protection
  enable_turnstile?: boolean;
  /**
   * When true, valid submissions also create/upsert a site Member (subscriber).
   * Backend contract (part-14 rust lane): wire field name is snake_case `add_to_members`.
   */
  add_to_members?: boolean;
}

export interface FormStep {
  id: string;
  name: string;
  description?: string;
  order: number;
  condition?: Record<string, unknown>;
  fields: FormField[];
}

export interface FormField {
  id: string;
  name: string;
  label: string;
  field_type: FormFieldType;
  order: number;
  required: boolean;
  placeholder?: string;
  help_text?: string;
  default_value?: string;
  validation_rules?: ValidationRules;
  ui_options?: Record<string, unknown>;
  options: FieldOption[];
}

export type FormFieldType =
  | 'text'
  | 'email'
  | 'url'
  | 'tel'
  | 'number'
  | 'textarea'
  | 'select'
  | 'multi_select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'time'
  | 'hidden'
  | 'heading'
  | 'paragraph';

export interface ValidationRules {
  min_length?: number;
  max_length?: number;
  min?: number;
  max?: number;
  pattern?: string;
}

export interface FieldOption {
  id: string;
  label: string;
  value: string;
  order: number;
}

export interface SubmissionNotification {
  id: string;
  channel: 'admin' | 'confirmation' | 'webhook';
  state: 'reserved' | 'pending' | 'sending' | 'sent' | 'failed_no_effect' | 'unknown' | 'cancelled';
  revision: number;
  attempt: number;
  retry_at: number | null;
  completed_at: number | null;
  failure_code: string | null;
  can_retry: boolean;
}
export function notificationStatusText(row: SubmissionNotification): string {
  if (row.state === 'unknown') return 'Unknown outcome — do not retry';
  if (row.state === 'sent') return 'Sent';
  if (row.failure_code === 'delivery_paused') return 'Paused — notification delivery is disabled';
  return { reserved: 'Reserved — not sent', pending: 'Pending — not sent', sending: 'Sending — awaiting confirmation', failed_no_effect: 'Failed before sending', cancelled: 'Cancelled' }[row.state];
}
export function canRetryNotification(row: SubmissionNotification): boolean {
  return row.can_retry && row.failure_code !== 'delivery_paused' && !['unknown', 'sending', 'sent'].includes(row.state);
}
const notificationPath = (siteSlug: string, formId: string, submissionId: string) => `/v1/sites/${encodeURIComponent(siteSlug)}/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}/notifications`;

// ============================================================================
// Request Types
// ============================================================================

export interface CreateFormData {
  name: string;
  slug: string;
  description?: string;
  settings?: FormSettings;
  steps: CreateStepData[];
}

export interface CreateStepData {
  name: string;
  description?: string;
  order?: number;
  condition?: string;
  fields: CreateFieldData[];
}

export interface CreateFieldData {
  name: string;
  label: string;
  field_type: FormFieldType;
  order?: number;
  required?: boolean;
  placeholder?: string;
  help_text?: string;
  default_value?: string;
  validation_rules?: ValidationRules;
  options?: CreateOptionData[];
}

export interface CreateOptionData {
  label: string;
  value: string;
  order?: number;
}

// ============================================================================
// Response Types (aliases using ApiResponse)
// ============================================================================

export type FormsListResponse = ApiResponse<FormSummary[]>;
export type FormResponse = ApiResponse<Form>;
export type FormHtmlResponse = ApiResponse<{ html: string }>;

// ============================================================================
// Submissions Types
// ============================================================================

export interface ResolvedFieldValue {
  field_id: string;
  name: string;
  label: string;
  value: unknown;
}

export interface SubmissionDetail {
  id: string;
  form_id: string;
  data: ResolvedFieldValue[];
  metadata: unknown | null;
  completed_at: string | null;
  created_at: string;
}

export type SubmissionsListResponse = ApiResponse<{
  submissions: SubmissionDetail[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}>;

export interface ListSubmissionsParams {
  page?: number;
  per_page?: number;
  from_date?: string;
  to_date?: string;
  search?: string;
  search_field?: string;
}

// ============================================================================
// API
// ============================================================================

export const formsApi = {
  notificationStatus: (siteSlug: string, formId: string, submissionId: string) => api.get<ApiResponse<SubmissionNotification[]>>(notificationPath(siteSlug, formId, submissionId)),
  retryNotification: (siteSlug: string, formId: string, submissionId: string, deliveryId: string, expectedRevision: number) => api.post<ApiResponse<null>>(`${notificationPath(siteSlug, formId, submissionId)}/${encodeURIComponent(deliveryId)}/retry`, { expected_revision: expectedRevision }),
  /** List forms for a site */
  list: (siteSlug: string) =>
    api.get<FormsListResponse>(`/v1/sites/${siteSlug}/forms`),

  /** Get a form by ID with all steps and fields */
  get: (siteSlug: string, formId: string) =>
    api.get<FormResponse>(`/v1/sites/${siteSlug}/forms/${formId}`),

  /** Get a form by slug */
  getBySlug: (siteSlug: string, slug: string) =>
    api.get<unknown>(`/v1/sites/${siteSlug}/forms/by-slug/${slug}`),

  /** Create a new form */
  create: (siteSlug: string, data: CreateFormData) =>
    api.post<FormResponse>(`/v1/sites/${siteSlug}/forms`, data),

  /** Update an existing form */
  update: (siteSlug: string, formId: string, data: CreateFormData) =>
    api.put<FormResponse>(`/v1/sites/${siteSlug}/forms/${formId}`, data),

  /** Delete a form */
  delete: (siteSlug: string, formId: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/forms/${formId}`),

  /** Get rendered HTML for a form */
  getHtml: (siteSlug: string, formId: string) =>
    api.get<FormHtmlResponse>(`/v1/sites/${siteSlug}/forms/${formId}/html`),

  /** Get submissions for a form */
  getSubmissions: (siteSlug: string, formId: string, params?: ListSubmissionsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString());
    if (params?.from_date) searchParams.set('from_date', params.from_date);
    if (params?.to_date) searchParams.set('to_date', params.to_date);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.search_field) searchParams.set('search_field', params.search_field);
    const query = searchParams.toString();
    const url = `/v1/sites/${siteSlug}/forms/${formId}/submissions${query ? `?${query}` : ''}`;
    return api.get<SubmissionsListResponse>(url);
  },

  /** Delete (soft) a single submission */
  deleteSubmission: (siteSlug: string, formId: string, submissionId: string) =>
    api.delete<ApiResponse<null>>(
      `/v1/sites/${siteSlug}/forms/${formId}/submissions/${submissionId}`
    ),
};
